import React, { createContext, useState, useContext } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createAppTheme } from "../theme/theme";

// Create the theme context
const ThemeContext = createContext();

// Custom hook for using the theme context
export const useTheme = () => useContext(ThemeContext);

// Default color palettes
export const colorPalettes = {
  blue: { primary: "#1976d2", secondary: "#ff9800" },
  green: { primary: "#2e7d32", secondary: "#f44336" },
  purple: { primary: "#7b1fa2", secondary: "#ffc107" },
  teal: { primary: "#00796b", secondary: "#e91e63" },
  indigo: { primary: "#3f51b5", secondary: "#ff5722" },
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Get initial mode from localStorage or system preference
  const getInitialMode = () => {
    const saved = localStorage.getItem("themeMode");
    if (saved === "light" || saved === "dark") return saved;
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
    return "light";
  };

  // Get saved density preference or use default
  const getInitialDensity = () => {
    const savedDensity = localStorage.getItem("themeDensity");
    if (
      savedDensity &&
      ["compact", "standard", "comfortable"].includes(savedDensity)
    ) {
      return savedDensity;
    }
    return "standard";
  };

  // Get saved custom palettes
  const getCustomPalettes = () => {
    const savedCustomPalettes = localStorage.getItem("customColorPalettes");
    if (savedCustomPalettes) {
      try {
        return JSON.parse(savedCustomPalettes);
      } catch (e) {
        console.error("Failed to parse custom palettes", e);
        return {};
      }
    }
    return {};
  };

  // Get saved color palette preference or use default
  const getInitialColorPalette = () => {
    const savedPalette = localStorage.getItem("themeColorPalette");
    const customPalettes = getCustomPalettes();

    if (
      savedPalette &&
      (colorPalettes[savedPalette] || customPalettes[savedPalette])
    ) {
      return savedPalette;
    }
    return "blue"; // Default palette
  };

  // State for the current theme mode and density
  const [mode, setMode] = useState(getInitialMode);
  const [density, setDensity] = useState(getInitialDensity);
  const [colorPalette, setColorPalette] = useState(getInitialColorPalette);
  const [customPalettes, setCustomPalettes] = useState(getCustomPalettes);

  // Get current palette colors
  const getCurrentPaletteColors = () => {
    if (colorPalettes[colorPalette]) {
      return colorPalettes[colorPalette];
    } else if (customPalettes[colorPalette]) {
      return customPalettes[colorPalette];
    }
    return colorPalettes.blue; // Default fallback
  };

  // Create the MUI theme based on the current mode, density, and color palette
  const theme = React.useMemo(() => {
    const paletteColors = getCurrentPaletteColors();
    return createAppTheme(
      mode,
      density,
      paletteColors.primary,
      paletteColors.secondary
    );
  }, [mode, density, colorPalette, customPalettes]);

  // Toggle theme mode
  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem("themeMode", next);
      return next;
    });
  };

  // Set specific theme mode
  const setThemeMode = (newMode) => {
    if (newMode === "dark" || newMode === "light") {
      setMode(newMode);
      localStorage.setItem("themeMode", newMode);
    }
  };

  // Set UI density
  const setThemeDensity = (newDensity) => {
    if (["compact", "standard", "comfortable"].includes(newDensity)) {
      localStorage.setItem("themeDensity", newDensity);
      setDensity(newDensity);
    }
  };

  // Set color palette
  const setThemeColorPalette = (newPalette) => {
    if (colorPalettes[newPalette] || customPalettes[newPalette]) {
      localStorage.setItem("themeColorPalette", newPalette);
      setColorPalette(newPalette);
    }
  };

  // Add custom color palette
  const addCustomPalette = (name, primaryColor, secondaryColor) => {
    if (!name || !primaryColor || !secondaryColor) {
      return false;
    }

    const updatedPalettes = {
      ...customPalettes,
      [name]: {
        primary: primaryColor,
        secondary: secondaryColor,
      },
    };

    localStorage.setItem(
      "customColorPalettes",
      JSON.stringify(updatedPalettes)
    );
    setCustomPalettes(updatedPalettes);
    return true;
  };

  // Update custom color palette
  const updateCustomPalette = (name, primaryColor, secondaryColor) => {
    if (!customPalettes[name]) {
      return false;
    }

    const updatedPalettes = {
      ...customPalettes,
      [name]: {
        primary: primaryColor || customPalettes[name].primary,
        secondary: secondaryColor || customPalettes[name].secondary,
      },
    };

    localStorage.setItem(
      "customColorPalettes",
      JSON.stringify(updatedPalettes)
    );
    setCustomPalettes(updatedPalettes);
    return true;
  };

  // Delete custom color palette
  const deleteCustomPalette = (name) => {
    if (!customPalettes[name]) {
      return false;
    }

    const updatedPalettes = { ...customPalettes };
    delete updatedPalettes[name];

    localStorage.setItem(
      "customColorPalettes",
      JSON.stringify(updatedPalettes)
    );
    setCustomPalettes(updatedPalettes);

    // If the current palette was deleted, switch to default
    if (colorPalette === name) {
      setThemeColorPalette("blue");
    }

    return true;
  };

  return (
    <ThemeContext.Provider
      value={{
        mode,
        density,
        colorPalette,
        customPalettes,
        toggleTheme,
        setThemeMode,
        setThemeDensity,
        setThemeColorPalette,
        addCustomPalette,
        updateCustomPalette,
        deleteCustomPalette,
        availableColorPalettes: { ...colorPalettes, ...customPalettes },
      }}
    >
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
